<template>
  <div v-if="show" class="bal-modal" @click="hide" @keyup.esc="hide">
    <transition name="modal-bg" mode="out-in" appear>
      <div v-if="showContent" class="modal-bg" @click="hide" />
    </transition>
    <div class="content-container">
      <AnimatePresence
        :initial="{ opacity: 0, translateY: 7.5 }"
        :animate="{
          opacity: 1,
          translateY: 0,
          delay: 100
        }"
        :exit="{ opacity: 0, translateY: 15 }"
        :isVisible="showContent"
        @on-exit="$emit('close')"
        class="flex justify-center w-full"
      >
        <div :class="['content', widthClasses]" @click.stop>
          <BalCard
            :title="title"
            shadow="lg"
            :no-pad="noPad"
            :no-content-pad="noContentPad"
            :rounded="rounded"
            :bgColor="bgColor"
            class="modal-card"
            no-border
          >
            <template v-if="$slots.header" v-slot:header>
              <slot name="header" />
            </template>
            <slot />
            <template v-if="$slots.footer" v-slot:footer>
              <slot name="footer" />
            </template>
          </BalCard>
        </div>
      </AnimatePresence>
    </div>
  </div>
</template>

<script lang="ts">
import BalCard from '../BalCard/BalCard.vue';
import AnimatePresence from '../../animate/AnimatePresence.vue';
import { defineComponent, ref, toRefs, watch, computed } from 'vue';

export default defineComponent({
  name: 'BalModal',

  components: {
    BalCard,
    AnimatePresence
  },

  props: {
    show: { type: Boolean, default: false },
    title: { type: String, default: '' },
    noPad: { type: Boolean, default: false },
    noContentPad: { type: Boolean, default: false },
    rounded: { type: String, default: '' },
    bgColor: { type: String, default: '' },
    width: { type: String, default: 'md' }
  },

  setup(props) {
    const { show } = toRefs(props);
    const showContent = ref(show.value);

    // Watchers
    watch(show, newVal => {
      showContent.value = newVal;
    });

    // Methods
    function hide(): void {
      showContent.value = false;
    }

    // Computed
    const widthClasses = computed(() => `width-${props.width}`);

    return {
      widthClasses,
      showContent,
      hide
    };
  }
});
</script>

<style scoped>
.bal-modal {
  @apply top-0 left-0 fixed h-screen w-full z-40;
}

.content-container {
  @apply flex h-screen items-end items-center justify-center;
}

.content {
  @apply relative w-full h-3/4 sm:h-auto max-h-screen overflow-hidden;
}

.width-xl {
  max-width: 576px;
}
.width-lg {
  max-width: 512px;
}
.width-md {
  max-width: 448px;
}
.width-sm {
  max-width: 384px;
}

.modal-bg {
  @apply absolute h-full w-full bg-black bg-opacity-80;
}

.modal-card {
  @apply mx-auto h-full;
}

.modal-bg-enter-active {
  transition: all 0.3s ease-in-out;
}

.modal-bg-leave-active {
  transition: all 0.3s ease-in-out;
}

.modal-bg-enter-from,
.modal-bg-leave-to {
  background: transparent;
}
</style>
